import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Phone, MapPin } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumbs from "./Breadcrumbs";
import SEO from "./SEO";
import ContentSections from "./ContentSections";
import ReviewedBy from "./ReviewedBy";
import HurricaneLeadForm from "./HurricaneLeadForm";
import StormFacts, { StormFactsBrief } from "./StormFacts";
import { nwflPages } from "../data/nwfl";
import { businessInfo } from "../mock";
import {
  localBusinessSchema,
  serviceSchema,
  faqSchema,
  breadcrumbSchema,
} from "../utils/schemas";

const NwflPage = () => {
  const { slug } = useParams();
  const page = nwflPages[slug];
  if (!page) return <Navigate to="/" replace />;

  const crumbs = [{ name: page.breadcrumbLabel, path: `/${page.slug}` }];
  const schemas = [
    localBusinessSchema({ areaServed: [`${page.city} FL`, "Northwest Florida"] }),
    serviceSchema({ name: page.h1, description: page.metaDescription, slug: page.slug }),
    breadcrumbSchema([{ name: "Home", path: "/" }, ...crumbs]),
    ...(page.faqs && page.faqs.length ? [faqSchema(page.faqs)] : []),
  ];

  const isHub = page.type === "geo-hub" || page.type === "storm-hub";

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/${page.slug}`}
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs items={crumbs} />

      {/* Hero */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="h-4 w-4 text-[#E11D26]" />
            <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
              {page.eyebrow}
            </span>
          </div>
          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-black uppercase leading-[1.03]"
            style={{ fontFamily: "'Anton','Oswald',sans-serif" }}
          >
            {page.h1}
          </h1>
          <p className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed max-w-3xl">
            {page.intro}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="#hurricane-lead"
              data-testid="nwfl-cta-review"
              className="group inline-flex items-center justify-center gap-3 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <span className="text-sm">Get Your Hurricane Claim Reviewed</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={`tel:${businessInfo.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm">Speak With a Public Adjuster</span>
            </a>
          </div>
        </div>
      </section>

      {isHub ? <StormFacts city={page.city} /> : <StormFactsBrief city={page.city} stormHub={page.stormHub} />}

      <ContentSections sections={page.sections} />

      {/* Hub quick links */}
      {isHub && (
        <section className="py-10 bg-[#0A0A0A] border-b border-white/10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { to: `/${page.city.toLowerCase()}-hurricane-damage-claims`, label: "Hurricane Damage" },
                { to: `/${page.city.toLowerCase()}-roof-damage-claims`, label: "Roof Damage" },
                { to: `/${page.city.toLowerCase()}-water-damage-claims`, label: "Water Damage" },
                { to: `/${page.city.toLowerCase()}-flood-damage-claims`, label: "Flood & Storm Surge" },
                { to: `/${page.city.toLowerCase()}-denied-insurance-claim`, label: "Denied Claims" },
                { to: `/${page.city.toLowerCase()}-underpaid-insurance-claim`, label: "Underpaid Claims" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nwfl-hublink-${l.to.replace(/\//g, "")}`}
                  className="group flex items-center justify-between gap-4 bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] p-4 transition-colors"
                >
                  <span className="text-white font-bold text-sm uppercase tracking-wide">{l.label}</span>
                  <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lead form */}
      <section id="hurricane-lead" className="py-14 md:py-16 bg-black border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <HurricaneLeadForm leadSource={page.leadSource} region={page.region} page={page.slug} />
        </div>
      </section>

      {/* FAQ (storm hubs) */}
      {page.faqs && page.faqs.length > 0 && (
        <section className="py-14 md:py-16 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2
              className="text-2xl sm:text-3xl font-black text-white uppercase"
              style={{ fontFamily: "'Anton','Oswald',sans-serif" }}
            >
              Hurricane Claim FAQ
            </h2>
            <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
              {page.faqs.map((f, i) => (
                <details key={i} className="group py-5" data-testid={`nwfl-faq-${i}`}>
                  <summary className="flex justify-between items-start cursor-pointer list-none gap-6">
                    <h3 className="text-white text-base sm:text-lg font-bold uppercase tracking-wide leading-snug">
                      {f.question}
                    </h3>
                    <span className="text-[#E11D26] text-xl group-open:rotate-45 transition-transform shrink-0">+</span>
                  </summary>
                  <p className="mt-4 text-white/75 leading-relaxed text-sm sm:text-base">{f.answer}</p>
                </details>
              ))}
            </div>
            <p className="mt-6 text-white/45 text-xs italic">
              This information is general and educational, not legal advice or a coverage
              determination. Coverage depends on your policy and the cause of loss.
            </p>
          </div>
        </section>
      )}

      <ReviewedBy />

      {/* Regional cross-link (Pensacola <-> Destin) */}
      <section className="py-10 bg-black border-t border-white/10" data-testid="nwfl-regional-crosslink">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-white/70 text-sm sm:text-base">
            Claim Beast also assists property owners across Northwest Florida.{" "}
            {page.city === "Destin" ? (
              <>
                <Link to="/hurricane-isaias-pensacola" className="text-[#E11D26] hover:text-white underline underline-offset-2 font-semibold">
                  Hurricane Isaias claim help in Pensacola
                </Link>
                {" · "}
                <Link to="/public-adjuster-pensacola" className="text-[#E11D26] hover:text-white underline underline-offset-2 font-semibold">
                  Pensacola public adjuster
                </Link>
              </>
            ) : (
              <>
                <Link to="/hurricane-isaias-destin" className="text-[#E11D26] hover:text-white underline underline-offset-2 font-semibold">
                  Hurricane Isaias claim help in Destin
                </Link>
                {" · "}
                <Link to="/public-adjuster-destin" className="text-[#E11D26] hover:text-white underline underline-offset-2 font-semibold">
                  Destin &amp; Emerald Coast public adjuster
                </Link>
              </>
            )}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NwflPage;
